'use client'

import '@aws-amplify/ui-react/styles.css'
import 'maplibre-gl/dist/maplibre-gl.css'

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Amplify } from '@aws-amplify/core'
import { Box, Center, Spinner, Text } from '@chakra-ui/react'
import { createMap } from 'maplibre-gl-js-amplify'
import maplibregl, { Map as MapLibreMap, Popup } from 'maplibre-gl'
import apiClient from 'helpers/clients/apiClient'
import LocationsMapPopup from './components/LocationPopup'
import { SortingInfo } from 'appTypes/mapper'

export interface MapLocation {
  id?: string | number
  name: string
  latitude: number
  longitude: number
  address?: string
  rating?: number
  distanceKm?: number
  travelTimeMin?: number
  phoneNumber?: string
  storeUrl?: string
  opensInFuture?: boolean
  opensInFutureDate?: string // ISO date string
  locationArticleId?: string | number
}

type Props = {
  id: number | string
  locations: MapLocation[]
  sortingInfo?: SortingInfo | undefined
  fitToLocations?: boolean
}

const DEFAULT_CENTER: [number, number] = [10, 59]
const DEFAULT_ICON_URL = '/icons/map/location_head_icon.svg'
const DEFAULT_FUTURE_ICON_URL = '/icons/map/location_head_gray_icon.svg'
const DEFAULT_ICON_SIZE = 50

function fitToBounds(map: MapLibreMap, points: MapLocation[]) {
  const valid = points.filter((p) => Number.isFinite(p.longitude) && Number.isFinite(p.latitude))
  if (!valid.length) return
  if (valid.length === 1) {
    map.setCenter([valid[0].longitude, valid[0].latitude])
    map.setZoom(12)
    return
  }
  const bounds = new maplibregl.LngLatBounds()
  valid.forEach((p) => bounds.extend([p.longitude, p.latitude]))
  map.fitBounds(bounds, { padding: 48, duration: 600 })
}

function ensurePopupBaseStyles() {
  const id = 'leid-popup-styles'
  if (document.getElementById(id)) return
  const style = document.createElement('style')
  style.id = id
  style.textContent = `
    /* Transparent popup container */
    .leid-popup.maplibregl-popup,
    .leid-popup.mapboxgl-popup {
      background: transparent !important;
      box-shadow: none !important;
      border: none !important;
      z-index: 99999 !important;
    }
    /* Transparent content area */
    .leid-popup .maplibregl-popup-content,
    .leid-popup .mapboxgl-popup-content {
      background: transparent !important;
      box-shadow: none !important;
      border: none !important;
      padding: 0 !important;
      border-radius: 0 !important;
      max-width: none !important;
    }
    /* Hide the little triangle tip */
    .leid-popup .maplibregl-popup-tip,
    .leid-popup .mapboxgl-popup-tip {
      display: none !important;
    }
    /* Hide the X*/
    .leid-popup .maplibregl-popup-close-button,
    .leid-popup .mapboxgl-popup-close-button {
      display: none !important;
    }
  `
  document.head.appendChild(style)
}

export default function LocationsMap({ id, locations, sortingInfo, fitToLocations = true }: Props) {
  const [amplifyLoaded, setAmplifyLoaded] = React.useState(false)
  const mapRef = useRef<MapLibreMap | null>(null)
  const markersRef = useRef<maplibregl.Marker[]>([])
  const activePopupRef = useRef<Popup | null>(null)

  const userMarkerRef = useRef<maplibregl.Marker | null>(null)

  // Portal to allow popup to have chakra context
  const [portalEl, setPortalEl] = useState<HTMLElement | null>(null)
  const [portalLoc, setPortalLoc] = useState<MapLocation | null>(null)

  const loadAmplifyConfig = useCallback(async () => {
    if (!amplifyLoaded) {
      const mapConfig = await apiClient.getMapConfig()
      Amplify.configure(mapConfig.data)
      setAmplifyLoaded(true)
    }
  }, [amplifyLoaded])

  const upsertCustomerMarker = useCallback(
    (map: MapLibreMap) => {
      const lat = sortingInfo?.lat
      const lng = sortingInfo?.long
      if (!lat || !lng || !Number.isFinite(lat) || !Number.isFinite(lng)) {
        userMarkerRef.current?.remove()
        userMarkerRef.current = null
        return
      }

      const lngLat: [number, number] = [lng, lat]

      if (userMarkerRef.current) {
        userMarkerRef.current.setLngLat(lngLat)
        return
      }

      userMarkerRef.current = new maplibregl.Marker({
        anchor: 'center',
        color: '#d42b2bff',
      })
        .setLngLat(lngLat)
        .addTo(map)
    },
    [sortingInfo],
  )

  // Create markers (modern way) with custom SVG icons via `new Image()`
  const rebuildMarkers = useCallback((map: MapLibreMap, points: MapLocation[]) => {
    ensurePopupBaseStyles()

    // clear previous markers
    markersRef.current.forEach((m) => m.remove())
    markersRef.current = []

    points
      .filter((p) => Number.isFinite(p.longitude) && Number.isFinite(p.latitude))
      .forEach((loc) => {
        const popup = new maplibregl.Popup({
          offset: 16,
          closeOnClick: true,
          className: 'leid-popup',
        }).setHTML('<div id="leid-popup-root"></div>')

        popup.on('open', () => {
          const mount = popup.getElement().querySelector('#leid-popup-root') as HTMLElement | null
          if (mount) {
            setPortalEl(mount)
            setPortalLoc(loc)
            activePopupRef.current = popup
          }
        })
        popup.on('close', () => {
          setPortalLoc(null)
          setPortalEl(null)
          if (activePopupRef.current === popup) activePopupRef.current = null
        })

        const img = new Image()
        img.src = loc.opensInFuture ? DEFAULT_FUTURE_ICON_URL : DEFAULT_ICON_URL
        img.alt = loc.name
        img.width = DEFAULT_ICON_SIZE
        img.height = DEFAULT_ICON_SIZE
        img.style.width = `${DEFAULT_ICON_SIZE}px`
        img.style.height = `${DEFAULT_ICON_SIZE}px`
        img.style.userSelect = 'none'
        img.style.pointerEvents = 'auto'
        img.style.zIndex = loc.opensInFuture ? '0' : '5'

        const marker = new maplibregl.Marker({ element: img, anchor: 'bottom' })
          .setLngLat([loc.longitude, loc.latitude])
          .setPopup(popup)
          .addTo(map)

        markersRef.current.push(marker)
      })
  }, [])

  // Load Amplify
  useEffect(() => {
    loadAmplifyConfig()
  }, [loadAmplifyConfig])

  // Init map once
  useEffect(() => {
    if (!amplifyLoaded || mapRef.current) return
    ;(async () => {
      const map = await createMap({ container: `map-${id}`, center: DEFAULT_CENTER, zoom: 4 })
      map.addControl(new maplibregl.NavigationControl({}), 'top-left')

      rebuildMarkers(map, locations)
      upsertCustomerMarker(map)

      if (fitToLocations) fitToBounds(map, locations)

      mapRef.current = map
    })()

    return () => {
      const map = mapRef.current
      if (!map) return
      activePopupRef.current?.remove()
      markersRef.current.forEach((m) => m.remove())
      markersRef.current = []
      userMarkerRef.current?.remove()
      userMarkerRef.current = null
      map.remove()
      mapRef.current = null
      setPortalLoc(null)
      setPortalEl(null)
    }
  }, [amplifyLoaded, id, locations, fitToLocations, rebuildMarkers, upsertCustomerMarker])

  // Update markers when locations change
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    rebuildMarkers(map, locations)
    upsertCustomerMarker(map)
    if (fitToLocations) fitToBounds(map, locations)
  }, [locations, fitToLocations, rebuildMarkers, upsertCustomerMarker])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    upsertCustomerMarker(map)
  }, [sortingInfo, upsertCustomerMarker])

  if (!amplifyLoaded) {
    return (
      <Center minH="372px" w="100%">
        <Spinner mr={3} />
        <Text fontSize="lg">Loading…</Text>
      </Center>
    )
  }

  return (
    <>
      <Box id={`map-${id}`} w="100%" minH="372px" borderRadius="8px" overflow="hidden" />
      {portalEl && portalLoc
        ? createPortal(
            <LocationsMapPopup
              loc={portalLoc}
              onClose={() => {
                activePopupRef.current?.remove()
                setPortalLoc(null)
                setPortalEl(null)
              }}
            />,
            portalEl,
          )
        : null}
    </>
  )
}
